/**
 * Event Interest Button state management
 *
 * Manages the state of the user's interest status and provides the API for the DOM layer to use.
 */

import { fetchWithCSRF } from "../../../assets/javascripts/admin/common/helpers";

const INTEREST_STATUS_PATH = '/event/interest_status';
const MAX_IDS = 100;

/** Split an array into chunks of a given size.
 *
 * @param {Array} arr - the array to chunk
 * @param {number} size - the size of the chunks
 * @returns {Array[]} - the chunked array
 */
const chunk = (arr, size) => {
  const a = arr ?? [];
  const out = [];
  for (let i = 0; i < a.length; i += size) {
    out.push(a.slice(i, i + size));
  }
  return out;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** localStorage key for interest status cache. */
const INTEREST_STATUS_CACHE_KEY = 'slzr:interest-status';

/** @returns {string|null} */
const getCurrentUserId = () => {
  return window.LOCALIST_SETTINGS?.user_id
    ? String(window.LOCALIST_SETTINGS.user_id)
    : null;
};

// ---------------------------------------------------------------------------
// localStorage cache
// ---------------------------------------------------------------------------

/**
 * Read and validate the localStorage cache.
 * Returns null when absent, corrupted, or owned by a different user.
 *
 * @returns {{ user_id: string, updated_at: string, instances: Object, events: Object }|null}
 */
const readCache = () => {
  const userId = getCurrentUserId();

  if (!userId) {
    return null;
  }

  try {
    const raw = localStorage.getItem(INTEREST_STATUS_CACHE_KEY);

    if (!raw) {
      return null;
    }

    const cached = JSON.parse(raw);

    if (String(cached.user_id) !== userId) {
      localStorage.removeItem(INTEREST_STATUS_CACHE_KEY);
      return null;
    }

    const instances = cached.instances ?? {};
    const events = cached.events ?? {};
    return { ...cached, instances, events };
  } catch {
    return null;
  }
};

/**
 * Persist instances and events maps for the current user.
 *
 * @param {{ instances: Object, events: Object }} payload - instances (instanceId -> { event_id, attending }), events (eventId -> { attending })
 */
const writeCache = (payload) => {
  const userId = getCurrentUserId();

  if (!userId) {
    return;
  }

  const instances = payload?.instances ?? {};
  const events = payload?.events ?? {};

  try {
    localStorage.setItem(
      INTEREST_STATUS_CACHE_KEY,
      JSON.stringify({
        user_id: userId,
        updated_at: new Date().toISOString(),
        instances,
        events,
      }),
    );
  } catch {
    // Quota exceeded or private-browsing restriction — ignore silently.
  }
};

/**
 * Build the events map from the instances map: event_id is present only when at least one instance
 * references it; attending is true iff any such instance has attending true.
 *
 * @param {Object} instances - Map of instanceId -> { event_id: number|null, attending: boolean }
 * @returns {Object} Map of eventId -> { attending: boolean }
 */
const deriveEventsFromInstances = (instances) => {
  const events = {};
  const inst = instances ?? {};
  for (const entry of Object.values(inst)) {
    const eid =
      entry?.event_id !== null && entry?.event_id !== undefined
        ? String(entry.event_id)
        : null;
    if (eid) {
      events[eid] = { attending: events[eid]?.attending || entry.attending };
    }
  }
  return events;
};

/**
 * Update one instance entry and the corresponding event in the cache.
 * Event IDs are derived from instances; an event is removed only when no instances reference it.
 *
 * @param {string|number} instanceId
 * @param {string|number|null} eventId - event_id for this instance (for events map)
 * @param {boolean} attending
 */
const updateCacheEntry = (instanceId, eventId, attending) => {
  if (!instanceId) {
    return;
  }

  const cached = readCache();
  const instances = { ...(cached?.instances ?? {}) };
  const sid = String(instanceId);

  if (attending) {
    instances[sid] = {
      event_id:
        eventId !== null && eventId !== undefined ? Number(eventId) : null,
      attending: true,
    };
  } else {
    delete instances[sid];
  }

  const events = deriveEventsFromInstances(instances);
  writeCache({ instances, events });
};

// ---------------------------------------------------------------------------
// Network
// ---------------------------------------------------------------------------

/**
 * Normalize a single server status entry to cache shape.
 *
 * @param {Object} entry
 * @returns {{ event_id: number|null, attending: boolean }}
 */
const normalizeServerEntry = (entry) => ({
  event_id:
    entry?.event_id !== null && entry?.event_id !== undefined
      ? Number(entry.event_id)
      : null,
  attending: entry?.attending === true,
});

/**
 * Merge server status response into current instances map.
 * Requested instance IDs missing from the response are treated as not attending (removed).
 * All entries in serverInstances are applied (including those returned via event_ids).
 *
 * @param {Object} serverInstances - Raw statuses from API
 * @param {string[]} requestedInstanceIds - Instance IDs we requested
 * @param {Object} currentInstances - Existing instances from cache (will be copied)
 * @returns {Object} New instances map
 */
const mergeServerStatusIntoCache = (
  serverInstances,
  requestedInstanceIds,
  currentInstances,
) => {
  const instances = { ...(currentInstances ?? {}) };

  for (const id of requestedInstanceIds) {
    const sid = String(id);
    if (Object.hasOwn(serverInstances, sid)) {
      instances[sid] = normalizeServerEntry(serverInstances[sid]);
    } else {
      delete instances[sid];
    }
  }

  for (const sid of Object.keys(serverInstances)) {
    instances[sid] = normalizeServerEntry(serverInstances[sid]);
  }

  return instances;
};

/**
 * Fetch the current user's interest status from the server, update cache, then call applyToPage.
 *
 * @param {string[]} instanceIds - Instance IDs to request (event_instance_ids[])
 * @param {string[]} eventIds - Event IDs to request (event_ids[])
 * @param {function(Object, Object): void} applyToPage - (instances, events) to update the DOM
 */
const fetchAndApplyStatus = async (instanceIds, eventIds, applyToPage) => {
  if ((!instanceIds?.length && !eventIds?.length) || !getCurrentUserId()) {
    return;
  }

  const instanceChunks = chunk(instanceIds, MAX_IDS);
  const eventChunks = chunk(eventIds, MAX_IDS);
  const numBatches = Math.max(instanceChunks.length, eventChunks.length);

  const cached = readCache();
  let instances = { ...(cached?.instances ?? {}) };
  let events = deriveEventsFromInstances(instances);

  for (let i = 0; i < numBatches; i++) {
    const batchInstanceIds = instanceChunks[i] ?? [];
    const batchEventIds = eventChunks[i] ?? [];

    const params = new URLSearchParams();
    for (const id of batchInstanceIds) {
      params.append("event_instance_ids[]", id);
    }
    for (const id of batchEventIds) {
      params.append("event_ids[]", id);
    }

    try {
      const response = await fetchWithCSRF(
        `${INTEREST_STATUS_PATH}?${params}`,
        {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        },
      );

      if (!response.ok) {
        break;
      }

      const data = await response.json();
      const serverInstances = data.statuses ?? {};
      instances = mergeServerStatusIntoCache(
        serverInstances,
        batchInstanceIds,
        instances,
      );
      events = deriveEventsFromInstances(instances);
      writeCache({ instances, events });
    } catch {
      // Network error — leave current state intact.
    }
  }

  applyToPage(instances, events);
};

/**
 * POST to a confirm / unconfirm URL and return the parsed JSON response.
 *
 * @param {string} url
 * @param {string|undefined} instanceId
 * @returns {Promise<Object>}
 */
const postAttendance = async (url, instanceId) => {
  const body = new URLSearchParams({ return: location.href });

  if (instanceId) {
    body.set('instance_id', instanceId);
  }

  const response = await fetchWithCSRF(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/x-www-form-urlencoded",
    },
    credentials: "same-origin",
    body: body.toString(),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.json();
};

// ---------------------------------------------------------------------------
// Event dispatch (consumers listen for state changes)
// ---------------------------------------------------------------------------

/**
 * Dispatch `slzr:attendance:change` on document.
 *
 * @param {Object} detail
 */
const dispatchAttendanceChange = (detail) => {
  document.dispatchEvent(
    new CustomEvent("slzr:attendance:change", { detail, bubbles: true }),
  );
};

// ---------------------------------------------------------------------------
// API for DOM layer (storage event + going lightbox merge)
// ---------------------------------------------------------------------------

/**
 * Parse storage event for our cache key and user; return { instances, events } or null.
 *
 * @param {StorageEvent} storageEvent
 * @returns {{ instances: Object, events: Object }|null}
 */
const getStorageEventData = (storageEvent) => {
  if (
    storageEvent.key !== INTEREST_STATUS_CACHE_KEY ||
    !storageEvent.newValue
  ) {
    return null;
  }

  try {
    const data = JSON.parse(storageEvent.newValue);
    const userId = getCurrentUserId();
    if (!userId || String(data.user_id) !== userId) {
      return null;
    }
    return {
      instances: data.instances ?? {},
      events: data.events ?? {},
    };
  } catch {
    return null;
  }
};

/**
 * Merge going-lightbox response into cache; return { instances, events } for DOM to apply.
 *
 * @param {Object} data - JSON response (event_id, attended_instance_ids)
 * @param {string[]} formInstanceIds - instance IDs from the form
 * @returns {{ instances: Object, events: Object }}
 */
const mergeGoingLightboxResult = (data, formInstanceIds) => {
  const eventId =
    data.event_id !== null && data.event_id !== undefined
      ? String(data.event_id)
      : null;
  const attendedIds = Array.isArray(data.attended_instance_ids)
    ? data.attended_instance_ids.map((id) => String(id))
    : [];

  const cached = readCache();
  const instances = { ...(cached?.instances ?? {}) };
  const eidNum =
    eventId !== null && eventId !== undefined ? Number(eventId) : null;

  for (const id of formInstanceIds) {
    instances[id] = {
      event_id: eidNum,
      attending: attendedIds.includes(id),
    };
  }

  const events = deriveEventsFromInstances(instances);
  writeCache({ instances, events });
  return { instances, events };
};

// ---------------------------------------------------------------------------
// API for DOM layer
// ---------------------------------------------------------------------------

/**
 * Build the API object for the DOM layer. The bootstrap wires this to
 * attachListeners and runs cache apply + fetch.
 *
 * @returns {Object} Status API (getCurrentUserId, readCache, updateCacheEntry, postAttendance,
 *   dispatchAttendanceChange, getStorageEventData, mergeGoingLightboxResult)
 */
const createInterestApi = () => ({
  getCurrentUserId,
  readCache,
  updateCacheEntry,
  postAttendance,
  dispatchAttendanceChange,
  getStorageEventData,
  mergeGoingLightboxResult,
});

export { createInterestApi, fetchAndApplyStatus };
